import { createContext, useContext, useReducer } from 'react'
import { TextcutRectangle } from '../types'

export type MainLayoutState = {
  openPopup: boolean
  referenceGroup: TextcutRectangle[]
  selected: string
} & (
  | {
      status: 'loading' | 'ready'
    }
  | {
      status: 'error'
      error: Error
    }
)

const initialState: MainLayoutState = {
  openPopup: false,
  referenceGroup: [],
  selected: '',
  status: 'loading',
}

export type MainLayoutStateAction =
  | {
      type: 'SET_READY'
    }
  | {
      type: 'SET_ERROR'
      payload: Error
    }
  | {
      type: 'SET_OPEN_POPUP'
      payload: boolean
    }
  | {
      type: 'SET_REFERENCE_GROUP'
      payload: TextcutRectangle[]
    }
  | {
      type: 'SET_SELECTED'
      payload: string
    }
  | {
      type: 'BATCH'
      payload: Partial<Omit<MainLayoutState, 'status' | 'error'>>
    }

const reducer = (
  state: MainLayoutState,
  action: MainLayoutStateAction
): MainLayoutState => {
  switch (action.type) {
    case 'BATCH': {
      return { ...state, ...action.payload }
    }
    case 'SET_SELECTED': {
      return { ...state, selected: action.payload }
    }
    case 'SET_REFERENCE_GROUP': {
      return { ...state, referenceGroup: action.payload }
    }
    case 'SET_OPEN_POPUP': {
      return { ...state, openPopup: action.payload }
    }
    case 'SET_ERROR': {
      return { ...state, status: 'error', error: action.payload }
    }
    case 'SET_READY':
      return { ...state, status: 'ready' }
    default:
      return state
  }
}

const LayoutContext = createContext<{
  state: MainLayoutState
  dispatch: React.Dispatch<MainLayoutStateAction>
}>(null as any)

export const withLayoutContext = (Component: React.ComponentType) => {
  return function WrappedComponent(props: any) {
    const [state, dispatch] = useReducer(reducer, initialState)
    return (
      <LayoutContext.Provider value={{ state, dispatch }}>
        <Component {...props} />
      </LayoutContext.Provider>
    )
  }
}

export const useLayoutContext = () => {
  const context = useContext(LayoutContext)
  if (!context) {
    throw new Error('useLayoutContext must be used within a LayoutProvider')
  }
  return context
}
